import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { ChevronDown, ChevronRight, TrendingUp, AlertTriangle, Award } from "lucide-react";
import { PERIODS } from "./teacher/ClassAnalytics";

const NO_DATA = "Henüz yeterli veri yok";
const pct = (v) => (v == null ? null : `%${Math.round(v * 100)}`);
const STATUS = {
  strong: ["Güçlü", "bg-emerald-100 text-emerald-700"],
  medium: ["Orta", "bg-sky-100 text-sky-700"],
  needs_work: ["Geliştirilmeli", "bg-rose-100 text-rose-700"],
  insufficient: ["Yetersiz veri", "bg-slate-100 text-slate-500"],
};

function Delta({ v, suffix = "" }) {
  if (v == null) return null;
  const cls = v > 0 ? "text-emerald-600" : v < 0 ? "text-rose-600" : "text-slate-400";
  return <span className={`text-xs font-medium ${cls}`}>{v > 0 ? "+" : ""}{v}{suffix}</span>;
}

function Badge({ status, tid }) {
  const [l, c] = STATUS[status] || STATUS.insufficient;
  return <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${c}`} data-testid={tid}>{l}</span>;
}

function Card({ label, children, tid }) {
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white p-3 min-w-0">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="font-display text-xl text-[#0B1A35] mt-0.5" data-testid={tid}>{children}</div>
    </div>
  );
}

export function StudentReport({ url, onLoaded }) {
  const [period, setPeriod] = useState("all_time");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setError(null);
      try {
        const { data: d } = await api.get(url, { params: { period } });
        setData(d);
        onLoaded?.(d);
      } catch (e) {
        setError(e?.response?.data?.detail || "Rapor yüklenemedi");
      }
    })();
    // eslint-disable-next-line
  }, [url, period]);

  if (error) return <div className="text-sm text-rose-600" data-testid="report-error">{error}</div>;
  if (!data) return <div className="text-sm text-slate-500" data-testid="report-loading">Yükleniyor…</div>;
  const m = data.summary;
  const cmp = data.comparison;
  return (
    <div className="space-y-5" data-testid="student-report">
      <div className="flex flex-wrap gap-1.5" data-testid="report-periods">
        {PERIODS.map(([k, l]) => (
          <button key={k} onClick={() => setPeriod(k)} data-testid={`report-period-${k}`}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
              period === k ? "bg-[#0B1A35] text-white border-[#0B1A35]" : "bg-white border-[#E2E8F0] text-slate-600 hover:border-[#007BFF]"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Card label="Çözülen soru" tid="report-total">{m.total_questions} {cmp && <Delta v={cmp.questions_delta} />}</Card>
        <Card label="Doğru / Yanlış / Boş" tid="report-dyb">{m.correct} / {m.wrong} / {m.blank}</Card>
        <Card label="Başarı oranı" tid="report-rate">
          {pct(m.success_rate) ?? <span className="text-sm text-slate-400">{NO_DATA}</span>}{" "}
          {cmp && <Delta v={cmp.success_rate_delta_pp} suffix=" puan" />}
        </Card>
        <Card label="Ağırlıklı başarı" tid="report-weighted">
          {pct(m.weighted_success_rate) ?? <span className="text-sm text-slate-400">{NO_DATA}</span>}
        </Card>
      </div>

      <div className="grid sm:grid-cols-2 gap-2">
        <TopicChips icon={Award} title="Güçlü konular" items={data.strong_topics} cls="text-emerald-700" tid="report-strong" />
        <TopicChips icon={AlertTriangle} title="Daha fazla çalışılmalı" items={data.needs_work_topics} cls="text-rose-700" tid="report-needs-work" />
      </div>

      <Trend data={data} />
      <TopicTable topics={data.topics} hasCmp={!!cmp} min={data.min_answered_for_rate} />
      <RecentWrong items={data.recent_wrong} />
    </div>
  );
}

function TopicChips({ icon: Icon, title, items, cls, tid }) {
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white p-3" data-testid={tid}>
      <div className={`flex items-center gap-1.5 text-xs font-medium ${cls}`}><Icon size={13} /> {title}</div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {items.length ? items.map((n) => <span key={n} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{n}</span>)
          : <span className="text-xs text-slate-400">Yeterli veriyle belirlenen konu yok</span>}
      </div>
    </div>
  );
}

function Trend({ data }) {
  const rows = data.trend.map((w) => ({
    hafta: new Date(w.week_start).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", timeZone: "Europe/Istanbul" }),
    soru: w.total_questions,
    basari: w.success_rate == null ? null : Math.round(w.success_rate * 100),
  }));
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white p-4" data-testid="report-trend">
      <div className="flex items-center gap-1.5 text-sm font-medium text-[#0B1A35]"><TrendingUp size={15} className="text-[#007BFF]" /> Haftalık gelişim (son 8 hafta)</div>
      {!data.has_trend && (
        <p className="text-xs text-slate-500 mt-1" data-testid="report-trend-insufficient">
          Gelişim yönü için yeterli geçmiş veri yok; başarı çizgisi yalnızca en az {data.min_answered_for_rate} cevaplı haftalarda gösterilir.
        </p>
      )}
      <div className="h-56 mt-2">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="hafta" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="l" tick={{ fontSize: 11 }} allowDecimals={false} />
            <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
            <Tooltip formatter={(v, n) => (n === "basari" ? [`%${v}`, "Başarı"] : [v, "Soru"])} />
            <Bar yAxisId="l" dataKey="soru" fill="#BFDBFE" radius={[4, 4, 0, 0]} />
            <Line yAxisId="r" dataKey="basari" stroke="#0B1A35" strokeWidth={2} dot connectNulls={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function TopicTable({ topics, hasCmp, min }) {
  const [open, setOpen] = useState({});
  if (!topics.length) {
    return <div className="rounded-xl border border-[#E2E8F0] bg-white p-6 text-center text-sm text-slate-500" data-testid="report-no-topics">Bu dönemde çözülmüş soru yok.</div>;
  }
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white overflow-x-auto" data-testid="report-topics">
      <div className="px-4 pt-3 text-sm font-medium text-[#0B1A35]">Konu ve alt konu performansı</div>
      <p className="px-4 text-[11px] text-slate-500">En çok yanlış olan konular üstte. {min} cevaptan az konularda oran gösterilmez.</p>
      <table className="w-full text-sm mt-2">
        <thead className="text-[11px] text-slate-500 text-left">
          <tr><th className="px-4 py-1.5 font-normal">Konu</th><th className="py-1.5 font-normal">Soru</th><th className="py-1.5 font-normal">D / Y</th><th className="py-1.5 font-normal">Başarı</th><th className="py-1.5 pr-4 font-normal">Durum</th></tr>
        </thead>
        <tbody>
          {topics.map((t, i) => (
            <React.Fragment key={t.topic_id || `none-${i}`}>
              <tr className="border-t border-slate-100 cursor-pointer hover:bg-slate-50" onClick={() => setOpen({ ...open, [i]: !open[i] })}
                data-testid={`report-topic-${i}`}>
                <td className="px-4 py-2 min-w-[150px]"><span className="inline-flex items-center gap-1">{open[i] ? <ChevronDown size={13} /> : <ChevronRight size={13} />}{t.name}</span></td>
                <td className="py-2">{t.total_questions}</td>
                <td className="py-2 whitespace-nowrap">{t.correct} / <span className="text-rose-600">{t.wrong}</span></td>
                <td className="py-2 whitespace-nowrap">{pct(t.success_rate) ?? <span className="text-xs text-slate-400">—</span>} {hasCmp && <Delta v={t.comparison?.success_rate_delta_pp} suffix="p" />}</td>
                <td className="py-2 pr-4"><Badge status={t.status} tid={`report-topic-status-${i}`} /></td>
              </tr>
              {open[i] && t.subtopics.map((s, j) => (
                <tr key={`${i}-${j}`} className="bg-slate-50/70 text-[13px]" data-testid={`report-subtopic-${i}-${j}`}>
                  <td className="pl-10 pr-2 py-1.5 text-slate-600">{s.name}</td>
                  <td className="py-1.5">{s.total_questions}</td>
                  <td className="py-1.5 whitespace-nowrap">{s.correct} / <span className="text-rose-600">{s.wrong}</span></td>
                  <td className="py-1.5 whitespace-nowrap">{pct(s.success_rate) ?? <span className="text-xs text-slate-400">—</span>} {hasCmp && <Delta v={s.comparison?.success_rate_delta_pp} suffix="p" />}</td>
                  <td className="py-1.5 pr-4"><Badge status={s.status} /></td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecentWrong({ items }) {
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white p-4" data-testid="report-recent-wrong">
      <div className="text-sm font-medium text-[#0B1A35]">Son yanlış cevaplar</div>
      {items.length === 0 ? <p className="text-xs text-slate-400 mt-1">Bu dönemde yanlış cevap yok.</p> : (
        <ul className="mt-2 divide-y divide-slate-100">
          {items.map((w, i) => (
            <li key={i} className="py-1.5 flex items-center justify-between gap-2 text-sm">
              <span className="min-w-0 truncate"><span className="font-mono text-xs text-[#007BFF] mr-2">{w.ref || "—"}</span>{w.topic}{w.subtopic ? ` · ${w.subtopic}` : ""}</span>
              <span className="text-[11px] text-slate-400 whitespace-nowrap">{new Date(w.created_at).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" })}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
